import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  MAX_OPTICAL_JOBS_PER_ORDER,
  type BasketValidation,
  type OpticalLabSummary,
  type ValidatedBasketLine,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader } from '@/components/ui/Card';
import { useCurrency } from '@/hooks/useCurrency';
import {
  PAIR_STEPS,
  stepComplete,
  type BuilderDraft,
  type BuilderStep,
  type PairDraft,
} from '../builder/draft';
import type { BuilderProblem } from '../builder/refusals';
import { frameSizes } from '../builder/format';

interface ReviewStepProps {
  draft: BuilderDraft;
  validation: BasketValidation | undefined;
  isChecking: boolean;
  checkFailed: boolean;
  onRetry: () => void;
  problems: BuilderProblem[];
  /** Clinical prescription numbers by id, from the list the user chose from. */
  prescriptionNumbers: ReadonlyMap<string, string>;
  labs: OpticalLabSummary[];
  onEdit: (pairId: string, step: BuilderStep) => void;
  onAddPair: () => void;
  onRemovePair: (pairId: string) => void;
  onAcceptPrices: () => void;
}

/**
 * Step 4: every pair as it will be ordered, priced by the server.
 *
 * Every figure here — each line's price, the tax and the total — is the server's answer to the order as it stands, never the builder's own arithmetic. Anything that has moved since a price or a lens was chosen is said here, against the pair and the line it concerns, with a way back to fix it.
 */
export function ReviewStep({
  draft,
  validation,
  isChecking,
  checkFailed,
  onRetry,
  problems,
  prescriptionNumbers,
  labs,
  onEdit,
  onAddPair,
  onRemovePair,
  onAcceptPrices,
}: ReviewStepProps) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  const lines = new Map((validation?.lines ?? []).map((line) => [line.lineKey ?? '', line]));
  const changed = (validation?.lines ?? []).filter((line) => line.issues.includes('PRICE_CHANGED'));
  const orderProblems = problems.filter((problem) => problem.pairId === null);

  return (
    <div className="space-y-5">
      {isChecking ? (
        <p className="text-muted flex items-center gap-2 text-sm" role="status">
          <Loader2 className="size-4 animate-spin" aria-hidden />
          {t('opticalOrders.review.checking')}
        </p>
      ) : null}
      {checkFailed ? (
        <Alert tone="error">
          <span className="block">{t('opticalOrders.review.checkFailed')}</span>
          <Button variant="secondary" size="sm" className="mt-2" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : null}

      {changed.length > 0 ? (
        <Alert tone="warning">
          <span className="block font-medium">{t('opticalOrders.review.pricesChanged')}</span>
          <ul className="mt-1 space-y-0.5">
            {changed.map((line) => (
              <li key={line.lineKey ?? line.variantId}>
                <span className="font-mono" dir="ltr">
                  {line.sku}
                </span>{' '}
                {t('opticalOrders.review.priceWasNow', {
                  was: format(line.expectedUnitPriceMinor),
                  now: format(line.unitPriceMinor),
                })}
              </li>
            ))}
          </ul>
          <Button variant="secondary" size="sm" className="mt-2" onClick={onAcceptPrices}>
            {t('opticalOrders.review.acceptPrices')}
          </Button>
        </Alert>
      ) : null}

      {orderProblems.map((problem) => (
        <Alert key={problem.message.key} tone="error">
          {t(problem.message.key, problem.message.values)}
        </Alert>
      ))}
      {(validation?.customerIssues ?? [])
        .filter((issue) => issue === 'MISSING' || issue === 'INACTIVE')
        .map((issue) => (
          <Alert key={issue} tone="error">
            {t(`opticalOrders.review.customerIssues.${issue}`)}
          </Alert>
        ))}

      {draft.pairs.map((pair, index) => (
        <PairReview
          key={pair.id}
          pair={pair}
          number={index + 1}
          canRemove={draft.pairs.length > 1}
          lines={lines}
          problems={distinct([
            ...pairState(pair, index + 1),
            ...problems.filter((problem) => problem.pairId === pair.id),
          ])}
          prescriptionNumbers={prescriptionNumbers}
          labs={labs}
          onEdit={(step) => onEdit(pair.id, step)}
          onRemove={() => onRemovePair(pair.id)}
        />
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="secondary"
          onClick={onAddPair}
          disabled={draft.pairs.length >= MAX_OPTICAL_JOBS_PER_ORDER}
        >
          <Plus className="size-4" aria-hidden />
          {t('opticalOrders.pairs.add')}
        </Button>
        {draft.pairs.length >= MAX_OPTICAL_JOBS_PER_ORDER ? (
          <span className="text-muted text-sm">
            {t('opticalOrders.pairs.addLimit', { max: MAX_OPTICAL_JOBS_PER_ORDER })}
          </span>
        ) : null}
      </div>
      <p className="text-muted text-xs">{t('opticalOrders.pairs.labelHint')}</p>

      <Card>
        <CardHeader
          title={t('opticalOrders.review.summary')}
          subtitle={t('opticalOrders.review.summaryHint')}
        />
        <dl className="space-y-2 px-5 py-4">
          <Figure
            label={t('sales.fields.subtotal')}
            value={validation ? format(validation.subtotalMinor) : '—'}
          />
          <Figure
            label={t('sales.fields.taxShort')}
            value={validation ? format(validation.taxMinor) : '—'}
          />
          <div className="border-line border-t pt-2">
            <Figure
              label={t('sales.fields.total')}
              value={validation ? format(validation.totalMinor) : '—'}
              strong
            />
          </div>
        </dl>
      </Card>
    </div>
  );
}

/** One line per thing to fix: the draft and the server can both say a prescription is no longer available. */
function distinct(problems: BuilderProblem[]): BuilderProblem[] {
  const seen = new Set<string>();
  return problems.filter((problem) => {
    const key = `${problem.field}|${problem.message.key}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/** What the draft itself says is missing from a pair: a clinical prescription let go, or a step left unfinished. */
function pairState(pair: PairDraft, number: number): BuilderProblem[] {
  if (pair.prescription.clinicalUnavailable) {
    return [
      {
        pairId: pair.id,
        step: 'prescription',
        field: 'clinical',
        message: { key: 'opticalOrders.clinical.noLongerAvailable' },
      },
    ];
  }
  const unfinished = PAIR_STEPS.find((step) => !stepComplete(pair, step));
  return unfinished
    ? [
        {
          pairId: pair.id,
          step: unfinished,
          field: 'pair',
          message: { key: 'opticalOrders.pairs.finishPair', values: { number: String(number) } },
        },
      ]
    : [];
}

export function Figure({
  label,
  value,
  strong,
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={strong ? 'text-ink font-medium' : 'text-muted text-sm'}>{label}</dt>
      <dd className={strong ? 'text-ink text-lg font-semibold' : 'text-ink text-sm'} dir="ltr">
        {value}
      </dd>
    </div>
  );
}

function PairReview({
  pair,
  number,
  canRemove,
  lines,
  problems,
  prescriptionNumbers,
  labs,
  onEdit,
  onRemove,
}: {
  pair: PairDraft;
  number: number;
  canRemove: boolean;
  lines: ReadonlyMap<string, ValidatedBasketLine>;
  problems: BuilderProblem[];
  prescriptionNumbers: ReadonlyMap<string, string>;
  labs: OpticalLabSummary[];
  onEdit: (step: BuilderStep) => void;
  onRemove: () => void;
}) {
  const { t } = useTranslation();
  const { prescription, frame, lenses, fitting } = pair;
  const rxNumber = prescription.clinicalPrescriptionId
    ? prescriptionNumbers.get(prescription.clinicalPrescriptionId)
    : undefined;
  const lab = labs.find((entry) => entry.id === pair.labId);

  return (
    <Card>
      <CardHeader
        title={t('opticalOrders.pairs.pair', { number })}
        wrapAction
        action={
          canRemove ? (
            <Button variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="size-4" aria-hidden />
              {t('opticalOrders.pairs.remove', { number })}
            </Button>
          ) : undefined
        }
      />
      <div className="divide-line divide-y">
        <Row
          label={t('opticalOrders.steps.prescription')}
          onEdit={() => onEdit('prescription')}
          editLabel={t('opticalOrders.review.editFor', {
            section: t('opticalOrders.steps.prescription'),
            number,
          })}
        >
          {prescription.source === 'CLINICAL' ? (
            <span>
              {t('opticalOrders.source.clinical')}
              {rxNumber ? (
                <>
                  {' · '}
                  <span className="font-mono" dir="ltr">
                    {rxNumber}
                  </span>
                </>
              ) : null}
            </span>
          ) : (
            <span>
              {t('opticalOrders.source.external')}
              {prescription.external.externalReference.trim() ? (
                <>
                  {' · '}
                  <span className="font-mono" dir="ltr">
                    {prescription.external.externalReference.trim()}
                  </span>
                </>
              ) : null}
            </span>
          )}
        </Row>

        <Row
          label={t('opticalOrders.steps.frame')}
          onEdit={() => onEdit('frame')}
          editLabel={t('opticalOrders.review.editFor', {
            section: t('opticalOrders.steps.frame'),
            number,
          })}
        >
          {frame?.recommendationToken ? (
            <Badge tone="brand" className="mb-1">
              {t('opticalOrders.recommendation.badge')}
            </Badge>
          ) : null}
          {frame ? (
            <LineSummary
              name={`${frame.productName}${frame.variantName ? ` — ${frame.variantName}` : ''}`}
              sku={frame.sku}
              detail={[frame.details?.colorName, frameSizes(frame.details)]
                .filter(Boolean)
                .join(' · ')}
              line={lines.get(pair.lineKeys.frame)}
            />
          ) : null}
        </Row>

        {(['right', 'left'] as const).map((eye) => {
          const lens = lenses[eye];
          const abbreviation = eye === 'right' ? 'OD' : 'OS';
          return (
            <Row
              key={eye}
              label={
                <span>
                  <abbr
                    dir="ltr"
                    title={t(`medical.eyes.${abbreviation}`)}
                    className="font-mono no-underline"
                  >
                    {abbreviation}
                  </abbr>{' '}
                  {t('opticalOrders.review.lens')}
                </span>
              }
              onEdit={() => onEdit('lenses')}
              editLabel={t('opticalOrders.review.editFor', {
                section: `${abbreviation} ${t('opticalOrders.review.lens')}`,
                number,
              })}
            >
              {lens ? (
                <LineSummary
                  name={`${lens.productName}${lens.variantName ? ` — ${lens.variantName}` : ''}`}
                  sku={lens.sku}
                  detail={[
                    t(`catalog.lensType.${lens.details.lensType}`),
                    t(`opticalOrders.supply.${lens.details.supplyMode}`),
                    fitting[eye].trim()
                      ? t('opticalOrders.review.fittingValue', { value: fitting[eye].trim() })
                      : null,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                  line={lines.get(eye === 'right' ? pair.lineKeys.right : pair.lineKeys.left)}
                />
              ) : null}
            </Row>
          );
        })}

        <Row
          label={t('opticalOrders.lab.label')}
          onEdit={() => onEdit('lenses')}
          editLabel={t('opticalOrders.review.editFor', {
            section: t('opticalOrders.lab.label'),
            number,
          })}
        >
          <span>{lab ? lab.name : t('opticalOrders.lab.awaiting')}</span>
        </Row>
      </div>

      {problems.length > 0 ? (
        <ul className="border-line space-y-1 border-t px-5 py-3">
          {problems.map((problem, index) => (
            <li
              key={`${problem.field}-${index}`}
              className="flex flex-wrap items-center gap-2 text-sm text-red-700"
            >
              <span>{t(problem.message.key, problem.message.values)}</span>
              <Button variant="ghost" size="sm" onClick={() => onEdit(problem.step)}>
                {t('opticalOrders.review.fix')}
              </Button>
            </li>
          ))}
        </ul>
      ) : null}
    </Card>
  );
}

function Row({
  label,
  children,
  onEdit,
  editLabel,
}: {
  label: ReactNode;
  children: ReactNode;
  onEdit: () => void;
  editLabel: string;
}) {
  const { t } = useTranslation();
  // On a phone the label and its edit button share the first line and the content takes the full width below; from sm up the three sit in one row.
  return (
    <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-x-4 gap-y-1 px-5 py-3 sm:grid-cols-[7rem_minmax(0,1fr)_auto]">
      <div className="text-muted text-sm">{label}</div>
      <div className="text-ink col-span-2 min-w-0 text-sm sm:col-span-1 sm:col-start-2 sm:row-start-1">
        {children}
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onEdit}
        aria-label={editLabel}
        className="col-start-2 row-start-1 justify-self-end sm:col-start-3"
      >
        <Pencil className="size-4" aria-hidden />
        <span className="hidden sm:inline">{t('common.edit')}</span>
      </Button>
    </div>
  );
}

function LineSummary({
  name,
  sku,
  detail,
  line,
}: {
  name: string;
  sku: string;
  detail: string;
  line: ValidatedBasketLine | undefined;
}) {
  const { t } = useTranslation();
  const { format } = useCurrency();
  // Exactly what the server says about the line; a price change is shown above, with the way to accept it.
  const issues = (line?.issues ?? []).filter((issue) => issue !== 'PRICE_CHANGED');
  return (
    <div className="flex flex-wrap items-start gap-3">
      <div className="min-w-0 flex-1">
        <p className="font-medium break-words">{name}</p>
        <p className="text-muted text-xs">
          <span className="font-mono" dir="ltr">
            {sku}
          </span>
          {detail ? ` · ${detail}` : ''}
        </p>
        {issues.map((issue) => (
          <p key={issue} className="text-xs text-red-700">
            {t(`opticalOrders.review.issues.${issue}`, { count: line?.availableQuantity ?? 0 })}
          </p>
        ))}
      </div>
      <span className="shrink-0 font-medium whitespace-nowrap" dir="ltr">
        {line ? format(line.unitPriceMinor) : '—'}
      </span>
    </div>
  );
}
